import React from 'react';

import { CurrentLang } from 'common/utils/LocalisationUtils';

const PatientHome = React.lazy(() => import('common/views/Patient/PatientHome/PatientHome'));
const Patient = React.lazy(() => import('common/views/Patient/Patient/Patient'));
const PatientProfile = React.lazy(() => import('common/views/Patient/PatientProfile/PatientProfile'));
const PatientTreatment = React.lazy(() => import('common/views/Patient/PatientTreatment/PatientTreatment'));
const MTHistory = React.lazy(() => import('common/views/Patient/MTHistory/PatientMTHistory'));
const PatientTreatmentHistoryDetails = React.lazy(() => import('common/views/Patient/PatientTreatmentHistoryDetails/PatientTreatmentHistoryDetails'));
const Notes = React.lazy(() => import('common/views/Patient/Notes/Notes'));
const Note = React.lazy(() => import('common/views/Patient/Note/Note'));
const Medications = React.lazy(() => import('common/views/Patient/Medications/Medications'));
const ClinicalAssessments = React.lazy(() => import('common/views/Patient/ClinicalAssessments/ClinicalAssessments'));
const Diagnoses = React.lazy(() => import('common/views/Patient/Diagnoses/Diagnoses'));

const BuildTreatmentPlan = React.lazy(() => import('common/views/Treatment/BuildTreatmentPlan/BuildTreatmentPlan'));
const ViewIndividualTreatment = React.lazy(() => import('common/views/Treatment/ViewIndividualTreatment/ViewIndividualTreatment'));
const EditCombinedTreatment = React.lazy(() => import('common/views/Treatment/EditCombinedTreatment/EditCombinedTreatment'));
const EditTreatmentPlan = React.lazy(() => import('common/views/Treatment/EditTreatmentPlan/EditTreatmentPlan'));

const SystemLanguage = React.lazy(() => import('common/views/Admin/SystemLanguage/SystemLanguage'));
const Contact = React.lazy(() => import('common/views/Admin/Contact/Contact'));
const About = React.lazy(() => import('common/views/Admin/About/About'));

const Users = React.lazy(() => import('common/views/Admin/Users/Users'));
const User = React.lazy(() => import('common/views/Admin/User/User'));

const Clinics = React.lazy(() => import('common/views/Admin/Clinics/Clinics'));
const Clinic = React.lazy(() => import('common/views/Admin/Clinic/Clinic'));

const Organisations = React.lazy(() => import('common/views/Admin/Organisations/Organisations'));
const Organisation = React.lazy(() => import('common/views/Admin/Organisation/Organisation'));

const InsuranceProvider = React.lazy(() => import('common/views/Admin/InsuranceProvider/InsuranceProvider'));
const InsuranceProviders = React.lazy(() => import('common/views/Admin/InsuranceProviders/InsuranceProviders'));

const Physician = React.lazy(() => import('common/views/Admin/Physician/Physician'));
const Physicians = React.lazy(() => import('common/views/Admin/Physicians/Physicians'));

const ImportExport = React.lazy(() => import('common/views/Admin/ImportExport/ImportExport'));

// https://github.com/ReactTraining/react-router/tree/master/packages/react-router-config

export const emptyGUID = "00000000-0000-0000-0000-000000000000";
export const currentPatientPlaceholder = CurrentLang().MSG_0092;

export const deviceBaseRoute = '/Device'

export const deviceUsersRoute = `${deviceBaseRoute}/Admin/Users`
export const deviceUserRoute = `${deviceBaseRoute}/Admin/Users/:SystemUserGuid`

export const baseRoute = '/PDM'

// Not used but needed for the build
export const patientAnalyticsRoutesBase = ''
export const clinicAnalyticsRoutesBase = [];
export const clinicRoute = [];
export const deviceAnalyticsRoutesBase = [];
export const deviceRoute = [];
export const organisationAnalyticsRoutesBase = [];
export const organisationRoute = [];
export const treatersRoutesBase = [];
export const treatersRoute = [];
export const reportRoute = [];
export const reportPreviewRoute = [];

// Names of route parts
export const accuracyRoute = `Accuracy`
export const assessmentsRoute = `Assessments`
export const mtHistoryRoute = `MTHistory`
export const treatmentsRoute = `Treatments`
export const detailsRoute = `Details`

export const treaterRoute = `Treater`
export const patientRoute = `Patient`

export const baseRoutes = [
  { path: `${baseRoute}`, exact: true, name: '' },
  { path: `${baseRoute}/Contact`, exact: true, name: CurrentLang().home_contact_us,  component: Contact },
  { path: `${baseRoute}/About`, exact: true, name: CurrentLang().about,  component: About },
  { path: `${baseRoute}/UserProfile/:SystemUserGuid`, exact: true, name: CurrentLang().MSG_0101, component: User },
  { path: `${baseRoute}/SystemLanguage`, exact: true, name: CurrentLang().settings_language,  component: SystemLanguage }
];

export const patientRoutesBase = `${baseRoute}/Patients`

export const patientRoutes = [
  { path: patientRoutesBase, exact: true, name: CurrentLang().route_patient_search, component: PatientHome },

  { path: `${patientRoutesBase}/:PatientGuid`, exact: true, name: currentPatientPlaceholder, component: PatientProfile },
  { path: `${patientRoutesBase}/:PatientGuid/Patient/:PageName`, exact: true, name: "",  component: Patient },

  { path: `${patientRoutesBase}/:PatientGuid/Notes`, exact: true, name: CurrentLang().patient_note_history,  component: Notes },
  { path: `${patientRoutesBase}/:PatientGuid/Notes/:NotesGuid`, exact: true, name: CurrentLang().note,  component: Note },

  { path: `${patientRoutesBase}/:PatientGuid/Medications`, exact: true, name: CurrentLang().patient_medication_history,  component: Medications },
  { path: `${patientRoutesBase}/:PatientGuid/ClinicalAssessments`, exact: true, name: CurrentLang().clinical_assessments,  component: ClinicalAssessments },
  { path: `${patientRoutesBase}/:PatientGuid/Diagnoses`, exact: true, name: CurrentLang().edit_patient_diagnosis,  component: Diagnoses },

  { path: `${patientRoutesBase}/:PatientGuid/MTHistory`, exact: true, name: CurrentLang().patient_home_mt_history,  component: MTHistory },
  { path: `${patientRoutesBase}/:PatientGuid/Treatments`, exact: true, name: CurrentLang().patient_history_plan_title, component: PatientTreatment },
  { path: `${patientRoutesBase}/:PatientGuid/Treatments/History/:ProtocolGuid`, exact: true, name: CurrentLang().patient_home_treatment_archive, component: PatientTreatmentHistoryDetails },

  { path: `${patientRoutesBase}/:PatientGuid/BuildTreatmentPlan`, exact: true, name: CurrentLang().treatment_plan_edit_title, component: BuildTreatmentPlan },
  { path: `${patientRoutesBase}/:PatientGuid/BuildTreatmentPlan/CombinedTreatment/:TemplateId`, exact: true, name: CurrentLang().treatment_combined_treatment_templates, component: EditCombinedTreatment },
  { path: `${patientRoutesBase}/:PatientGuid/BuildTreatmentPlan/IndividualTreatment/:TemplateId`, exact: true, name: CurrentLang().treatment_individual_treatment_templates, component: ViewIndividualTreatment },

  // Define 3 routes to the same page - so we can pass in ProtocolGuid, TreatmentTemplateGuid or TreatmentPlanGuid
  { path: `${patientRoutesBase}/:PatientGuid/BuildTreatmentPlan/EditTreatmentPlan/:ProtocolGuid/Individual`, exact: true, name:CurrentLang().patient_home_edit_treatment_plan, component: EditTreatmentPlan },
  { path: `${patientRoutesBase}/:PatientGuid/BuildTreatmentPlan/EditTreatmentPlan/:TreatmentTemplateGuid/Combined`, exact: true, name: CurrentLang().patient_home_edit_treatment_plan, component: EditTreatmentPlan },
  { path: `${patientRoutesBase}/:PatientGuid/BuildTreatmentPlan/EditTreatmentPlan/:TreatmentPlanGuid/Edit`, exact: true, name: CurrentLang().patient_home_edit_treatment_plan, component: EditTreatmentPlan },
];

export const treatmentRoutesBase = `${baseRoute}/Treatment`

export const treatmentRoutes = [
  { path: `${treatmentRoutesBase}/BuildTreatmentPlan`, exact: true, name: CurrentLang().treatment_plan_edit_title, component: BuildTreatmentPlan },
  { path: `${treatmentRoutesBase}/BuildTreatmentPlan/CombinedTreatment/:TemplateId`, exact: true, name: CurrentLang().treatment_combined_treatment_templates, component: EditCombinedTreatment },
  { path: `${treatmentRoutesBase}/BuildTreatmentPlan/IndividualTreatment/:TemplateId`, exact: true, name: CurrentLang().treatment_individual_treatment_templates, component: ViewIndividualTreatment },
];

export const adminRoutesBase = `${baseRoute}/Admin`
  
export const adminUserRoutes = [
  { path: `${adminRoutesBase}/Users`, exact: true, name: CurrentLang().users,component: Users },
  { path: `${adminRoutesBase}/Users/:SystemUserGuid`, exact: true, name: CurrentLang().user, component: User },
];

export const adminClinicRoutes = [
  { path: `${adminRoutesBase}/Clinics`, exact: true, name: CurrentLang().clinics, component: Clinics },
  { path: `${adminRoutesBase}/Clinics/:ClinicGuid`, exact: true, name: CurrentLang().clinic,  component: Clinic },
];

export const adminOrganisationRoutes = [
  { path: `${adminRoutesBase}/Organisations`, exact: true, name: CurrentLang().organisations,  component: Organisations },
  { path: `${adminRoutesBase}/Organisations/:OrganisationGuid`, exact: true, name: CurrentLang().organisation, component: Organisation },
];

export const adminInsuranceProviderRoutes = [
  { path: `${adminRoutesBase}/InsuranceProviders`, exact: true, name:  CurrentLang().insurance_providers,  component: InsuranceProviders },
  { path: `${adminRoutesBase}/InsuranceProviders/:InsuranceProviderGuid`, exact: true, name:  CurrentLang().insurance_provider, component: InsuranceProvider },
];

export const adminPhysicianRoutes = [
  { path: `${adminRoutesBase}/Physicians`, exact: true, name:  CurrentLang().physicians,  component: Physicians },
  { path: `${adminRoutesBase}/Physicians/:PhysicianGuid`, exact: true, name:  CurrentLang().physician, component: Physician },
];

export const currentOrganisationRoute = [
  {path: `${adminRoutesBase}/Organisation`, exact: true, name: CurrentLang().organisation, component: Organisation} 
];

export const importRoute = [
  {path: `${adminRoutesBase}/Import`, exact: true, name: CurrentLang().import_export, component: ImportExport} 
];


export const contactRoute = `${baseRoute}/Contact`
export const aboutRoute = `${baseRoute}/About`

export const userProfileRoute = `${baseRoute}/UserProfile/Current`
export const systemLanguageRoute = `${baseRoute}/SystemLanguage`

export default baseRoutes;
